var __defProp = Object.defineProperty;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField = (obj, key, value) => __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
let generatedStepIndex = 0;
let trunkIndex = 0;
const TRUNK_PREFIX = "trunk-";
function buildSteps(items) {
  const stepsWithTrunks = group(items);
  const stepMap = /* @__PURE__ */ new Map();
  const stepsWithoutTrunks = [];
  const foundTrunks = [];
  function gatherMetadata() {
    for (const stepWithTrunk of stepsWithTrunks) {
      stepMap.set(stepWithTrunk.name, stepWithTrunk);
      if (stepWithTrunk.name.startsWith(TRUNK_PREFIX)) {
        foundTrunks.push(stepWithTrunk.name);
      } else {
        stepsWithoutTrunks.push(stepWithTrunk);
      }
    }
  }
  function removeTrunksFromDefinition() {
    for (const stepWithTrunk of stepsWithTrunks) {
      stepWithTrunk.next = detrunk(stepWithTrunk.next, stepMap);
    }
    for (const foundTrunk of foundTrunks) {
      stepMap.delete(foundTrunk);
    }
  }
  function connectBackwards() {
    for (const stepsWithoutTrunk of stepsWithoutTrunks) {
      const next = stepsWithoutTrunk.next;
      if (typeof next === "string") {
        const nextItem = stepMap.get(next);
        if (!nextItem) {
          throw new Error(
            `Could not find next item with name ${next} while connecting previous step, at ${stepsWithoutTrunk.name}`
          );
        }
        if (!nextItem.previous) {
          nextItem.previous = stepsWithoutTrunk.name;
        }
      }
      if (Array.isArray(next)) {
        for (const path of next) {
          const nextItem = stepMap.get(path.target);
          if (!nextItem) {
            throw new Error(
              `Could not find next item with name ${path.target} while connecting previous steps, at ${stepsWithoutTrunk.name}`
            );
          }
          if (!nextItem.previous) {
            nextItem.previous = [
              {
                target: stepsWithoutTrunk.name,
                enabled: typeof stepsWithoutTrunk.enabled === "undefined" ? true : stepsWithoutTrunk.enabled
              }
            ];
          } else if (Array.isArray(nextItem.previous)) {
            nextItem.previous.push({
              target: stepsWithoutTrunk.name,
              enabled: typeof stepsWithoutTrunk.enabled === "undefined" ? true : stepsWithoutTrunk.enabled
            });
          } else {
            throw new Error(
              `Tried connecting "previous" property of step ${nextItem.name} to step ${stepsWithoutTrunk.name},

                 but it already had the key defined as string.
 This would introduce ambiguity, define 

                 the "previous" key on step "${nextItem.name}" manually in the definition.`
            );
          }
        }
      }
    }
  }
  gatherMetadata();
  removeTrunksFromDefinition();
  connectBackwards();
  generatedStepIndex = 0;
  trunkIndex = 0;
  return {
    list: stepsWithoutTrunks,
    record: stepMap
  };
}
function group(items, options = {}) {
  if (isBranch(items.at(0))) {
    items = [trunk(), ...items];
  }
  if (options.name) {
    for (const item of items) {
      item.tags.push(options.name);
    }
  }
  let flattenedSteps = [];
  items.forEach((groupItem, index) => {
    if (isBranch(groupItem)) {
      const branchItemsWithMergedOptions = groupItem.items.map((child) => {
        return {
          ...child,
          tags: [...groupItem.tags, ...child.tags]
        };
      });
      flattenedSteps = [...flattenedSteps, ...branchItemsWithMergedOptions];
      return;
    }
    const nextItem = items.at(index + 1);
    flattenedSteps.push(groupItem);
    if (groupItem.next || !nextItem) {
      return;
    }
    if (nextItem && isBranch(nextItem)) {
      groupItem.next = nextItem.paths;
      return;
    }
    groupItem.next = nextItem.name;
  });
  if (options.enabled) {
    flattenedSteps.forEach((flattenedStep) => {
      flattenedStep.enabled = mergePredicates(
        flattenedStep.enabled,
        options.enabled
      );
    });
  }
  return flattenedSteps;
}
function split(branches) {
  const paths = [];
  let items = [];
  for (const branchEntry of branches) {
    const branchSteps = group(branchEntry.items, {
      enabled: branchEntry.enabled
    });
    const firstStep = branchSteps.at(0);
    if (!firstStep) {
      continue;
    }
    items = [...items, ...branchSteps];
    paths.push({
      target: firstStep.name,
      enabled: branchEntry.enabled
    });
  }
  return {
    items,
    paths,
    tags: []
  };
}
function branch(items, enabled) {
  return {
    items: Array.isArray(items) ? items : [items],
    enabled
  };
}
function step({
  name,
  ...restOptions
}) {
  return {
    name: name ?? `step-${generatedStepIndex++}`,
    ...restOptions,
    tags: []
  };
}
function stepTemplate({ name, ...restOptions }) {
  let localStepIndex = -1;
  const globalGeneratedStepIndex = generatedStepIndex++;
  return ({
    name: nameOverride,
    ...options
  } = {}) => {
    const isSuffixAdded = localStepIndex >= 0;
    const suffix = isSuffixAdded ? `-${localStepIndex}` : "";
    const prefix = name ?? `step-${globalGeneratedStepIndex}`;
    const generatedName = `${prefix}${suffix}`;
    const definition = {
      name: nameOverride ?? generatedName,
      ...restOptions,
      ...options,
      tags: []
    };
    if (!nameOverride) {
      localStepIndex++;
    }
    return definition;
  };
}
function detrunk(next, items) {
  if (typeof next === "string") {
    if (next.startsWith(TRUNK_PREFIX)) {
      const trunkItem = items.get(next);
      if (!trunkItem) {
        throw new Error(`Could not find trunk with name ${next}`);
      }
      return detrunk(trunkItem.next, items);
    }
    return next;
  }
  if (Array.isArray(next)) {
    const cleanPaths = [];
    for (const path of next) {
      if (!path.target.startsWith(TRUNK_PREFIX)) {
        cleanPaths.push(path);
      } else {
        const pathTarget = items.get(path.target);
        if (!pathTarget) {
          throw new Error(`Could not find target with name ${path.target}`);
        }
        const pathNextKey = detrunk(pathTarget.next, items);
        if (typeof pathNextKey === "string") {
          cleanPaths.push({
            target: pathNextKey,
            enabled: path.enabled
          });
        }
        if (Array.isArray(pathNextKey)) {
          for (const nestedPath of pathNextKey) {
            const enabled = mergePredicates(path.enabled, nestedPath.enabled);
            if (typeof enabled !== "undefined") {
              cleanPaths.push({
                target: nestedPath.target,
                enabled
              });
            }
          }
        }
      }
    }
    return cleanPaths;
  }
}
function mergePredicates(left, right) {
  if (typeof left === "undefined") {
    return right;
  }
  if (typeof right === "undefined") {
    return left;
  }
  return (context) => (typeof left === "boolean" ? left : left(context)) && (typeof right === "boolean" ? right : right(context));
}
function trunk() {
  return step({
    name: `${TRUNK_PREFIX}${trunkIndex++}`,
    Component: () => null
  });
}
function isBranch(item) {
  return Boolean(item) && typeof item !== "undefined" && "paths" in item;
}
const DEFAULT_LOGGER = {
  info: (line, ...data) => {
    console.info(`[FUNNEL CORE] ${line}`, ...data);
  },
  // eslint-disable-next-line no-console -- Debug log
  table: console.table
};
class Funnel {
  constructor(definition, options = {
    logger: {
      info: (line, ...data) => {
        console.info(`[FUNNEL] ${line}`, ...data);
      },
      // eslint-disable-next-line no-console -- Debug log
      table: console.table
    }
  }) {
    __publicField(this, "stepHistory", []);
    __publicField(this, "currentStep", null);
    this.definition = definition;
    this.options = options;
  }
  detectBrowserStep() {
    var _a, _b;
    const step2 = this.peekBrowserStep();
    this.debugSection(({ info }) => {
      info(`Detecting browser step, found: ${(step2 == null ? void 0 : step2.name) ?? "none"}`, step2);
      if (!step2) {
        info("No step found for the current path, logging funnel state");
        this.debug();
      }
    });
    if (!step2) {
      const location = (_b = (_a = this.definition).resolveLocation) == null ? void 0 : _b.call(_a);
      if (this.definition.basePath && location && !location.pathname.startsWith(this.definition.basePath)) {
        this.debugSection(({ info }) => {
          info(
            `No step was detected, but funnel was called on a path that didn't belong to it: ${location.pathname}, while the funnel base path was: ${this.definition.basePath ?? '"undefined"'}`
          );
        });
        return;
      }
      throw new Error("The current URL does not match any step in the funnel");
    }
    if (!this.checkPredicate(step2.enabled)) {
      const nextStep = this.calculateNextStep(step2, "backward");
      this.debugSection(({ info }) => {
        info(
          `Next step is disabled, trying to find next enabled step browser step, found: ${(nextStep == null ? void 0 : nextStep.name) ?? "none"}`,
          step2
        );
        if (!nextStep) {
          info(
            "No next step found for the disabled step, this will error out, logging funnel state"
          );
          this.debug();
        }
      });
      if (nextStep) {
        this.loadStep({
          step: nextStep,
          event: { changedFromDisabledStep: true }
        });
      } else {
        throw new Error(
          `Detected step ${step2.name} is not enabled, and there is no previous step to go back to`
        );
      }
    } else {
      this.loadStep({ step: step2 });
    }
  }
  peekBrowserStep() {
    var _a, _b;
    const location = (_b = (_a = this.definition).resolveLocation) == null ? void 0 : _b.call(_a);
    if (!location) {
      throw new Error(
        "Cannot detect browser step, because the location resolver is not defined, make sure your funnel definition has a `resolveLocation` function"
      );
    }
    for (const step2 of this.definition.steps.list) {
      const stepPath = `${this.definition.basePath || ""}${step2.uri || ""}`;
      const stepLocation = pathToURL(stepPath);
      const stepMatchesPath = this.trimTrailingSlashes(stepLocation.pathname) === this.trimTrailingSlashes(location.pathname);
      const stepMatchesSearch = isSearchSubsetOf(
        stepLocation.search,
        location.search
      );
      if (stepMatchesPath && stepMatchesSearch) {
        return step2;
      }
    }
  }
  trimTrailingSlashes(path) {
    return path.replace(/\/+$/, "");
  }
  stepForward() {
    const target = this.peekNextStep();
    if (target) {
      this.loadStep({ step: target });
    }
  }
  peekNextStep() {
    return this.currentStep ? this.calculateNextStep(this.currentStep) : null;
  }
  stepBack() {
    const previousStep = this.peekPreviousStep();
    if (!previousStep) {
      return;
    }
    this.loadStep({
      step: previousStep,
      options: { historyAction: "POP" }
    });
  }
  peekPreviousStep() {
    if (!this.currentStep) {
      return null;
    }
    const previousStep = this.calculateNextStep(this.currentStep, "backward");
    if (!previousStep) {
      return null;
    }
    if (!this.checkPredicate(previousStep.enabled)) {
      return this.calculateNextStep(previousStep, "backward");
    }
    return previousStep;
  }
  forceStep(name) {
    const step2 = this.getStep(name);
    if (!this.checkPredicate(step2.enabled)) {
      throw new Error(
        `Cannot force a step ${step2.name} that is currently not enabled`
      );
    }
    this.loadStep({ step: step2 });
  }
  loadStep({
    step: step2,
    options = { historyAction: "PUSH" },
    event
  }) {
    this.currentStep && options.historyAction === "PUSH" && this.stepHistory.push(this.currentStep);
    options.historyAction === "POP" && this.stepHistory.pop();
    this.currentStep = step2;
    this.debugSection(({ info }) => {
      info(
        `Funnel step loaded, onStepChange will be called with the following step: ${step2.name}`,
        step2
      );
    });
    if (this.definition.onStepChange) {
      this.definition.onStepChange(step2, {
        changedFromDisabledStep: false,
        ...event
      });
    }
  }
  /**
   * Takes a step and based on the direction, it will traverse the definition
   * and find a first enabled step to go to. If the direct previous step is
   * disabled, it will try to find a first enabled step in definition, and if
   * it fails, it will try to fall back to the step history. In case it fails
   * even then, user can handle the `null` return manually.
   */
  calculateNextStep(currentStep, direction = "forward") {
    const target = direction === "forward" ? currentStep.next : currentStep.previous;
    if (!target) {
      return null;
    }
    if (typeof target === "string") {
      const step2 = this.getStep(target);
      if (typeof step2.enabled !== "undefined" && !this.checkPredicate(step2.enabled)) {
        return this.calculateNextStep(step2, direction);
      }
      return step2;
    }
    for (const path of target) {
      if (this.checkPredicate(path.enabled)) {
        const step2 = this.getStep(path.target);
        if (typeof step2.enabled !== "undefined" && !this.checkPredicate(step2.enabled)) {
          return this.calculateNextStep(step2, direction);
        }
        return step2;
      }
    }
    if (target.length === 1 && direction === "backward") {
      const step2 = this.getStep(target[0].target);
      return this.calculateNextStep(step2, direction);
    }
    if (target.length > 0 && direction === "backward") {
      for (const step2 of this.stepHistory.reverse()) {
        if (this.checkPredicate(step2.enabled)) {
          return step2;
        }
      }
    }
    return null;
  }
  getStep(name) {
    const step2 = this.definition.steps.record.get(name);
    if (!step2) {
      throw new Error(`Step "${name}" not found in the funnel definition`);
    }
    return step2;
  }
  checkPredicate(predicate) {
    if (typeof predicate === "undefined") {
      return true;
    }
    if (typeof predicate === "boolean") {
      return predicate;
    }
    return predicate(this.context());
  }
  /**
   * Returns a step definition by its name together with some
   * debugging information. A helper function for testing.
   */
  peekStep(name) {
    const step2 = this.getStep(name);
    return {
      definition: step2,
      enabled: this.checkPredicate(step2.enabled)
    };
  }
  /**
   * Uses snapshot function to gather metadata about the funnel, then print JSON object.
   */
  dump() {
    console.log(JSON.stringify(this.snapshot(), null, 2));
  }
  /**
   * Uses snapshot function to gather metadata about the funnel, then logs the data.
   */
  debug() {
    console.log(this.snapshot());
  }
  /**
   * Gathers complete state of the funnel, useful for debugging.
   */
  snapshot() {
    var _a, _b, _c, _d;
    return {
      state: ((_b = (_a = this.definition).getState) == null ? void 0 : _b.call(_a)) ?? {},
      currentStep: this.currentStep,
      location: (_d = (_c = this.definition).resolveLocation) == null ? void 0 : _d.call(_c),
      stepHistory: this.stepHistory,
      definition: this.definition,
      stepList: this.definition.steps.list,
      stepRecord: this.definition.steps.record
    };
  }
  /**
   * Gathers metadata about the funnel, it passed to the funnel predicates.
   */
  context() {
    var _a, _b, _c, _d;
    return {
      state: ((_b = (_a = this.definition).getState) == null ? void 0 : _b.call(_a)) ?? {},
      currentStep: this.currentStep,
      location: (_d = (_c = this.definition).resolveLocation) == null ? void 0 : _d.call(_c),
      stepHistory: this.stepHistory
    };
  }
  debugSection(callback) {
    var _a, _b;
    if (this.options.logLevel === "debug") {
      callback({
        info: ((_a = this.options.logger) == null ? void 0 : _a.info) ?? DEFAULT_LOGGER.info,
        table: ((_b = this.options.logger) == null ? void 0 : _b.table) ?? DEFAULT_LOGGER.table
      });
    }
  }
}
class Internal__Funnel extends Funnel {
  constructor() {
    super(...arguments);
    /** Exposed for testing purposes, do not use in application code */
    __publicField(this, "internal__calculateNextStep", this.calculateNextStep.bind(this));
  }
}
function mockFunnel(steps, getState) {
  return new Internal__Funnel({
    steps: buildSteps(steps),
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- TODO: check linting
    getState: getState || (() => ({})),
    resolveLocation() {
      return new URL("", "https://__domain__.com");
    }
  });
}
function pathToURL(path) {
  let finalPath = path;
  if (!path.startsWith("/")) {
    finalPath = `/${path}`;
  }
  return new URL(finalPath, "https://__internal_do_no_use_domain__.com");
}
function isSearchSubsetOf(subsetSearch, search) {
  const subsetParams = new URLSearchParams(subsetSearch);
  const searchParams = new URLSearchParams(search);
  for (const [key, value] of subsetParams.entries()) {
    if (searchParams.get(key) !== value) {
      return false;
    }
  }
  return true;
}
export {
  Funnel,
  Internal__Funnel,
  branch,
  buildSteps,
  group,
  mockFunnel,
  split,
  step,
  stepTemplate
};
//# sourceMappingURL=index.js.map
